// PlatOverlay.tsx
// Map overlay component for displaying county plat data

import { PlatData, PlatLot, PlatEasement, PlatStreet } from '../../services/PlatDataService';
import { FloorPlanPlacementTool } from './FloorPlanPlacementTool';

export class PlatOverlay {
  private map: google.maps.Map;
  private platData: PlatData | null = null;
  private overlays: {
    lots: google.maps.Polygon[];
    easements: google.maps.Polygon[];
    streets: google.maps.Polyline[];
    labels: google.maps.Marker[];
    setbackLines: google.maps.Polyline[];
  } = {
    lots: [],
    easements: [],
    streets: [],
    labels: [],
    setbackLines: []
  };
  private isVisible: boolean = true;
  private selectedLot: PlatLot | null = null;
  private floorPlanTool: FloorPlanPlacementTool | null = null;

  constructor(map: google.maps.Map) {
    this.map = map;
  }

  /**
   * Load and display plat data on the map
   */
  public loadPlatData(platData: PlatData): void {
    this.clearOverlays();
    this.platData = platData;
    
    if (!this.isVisible) return;

    console.log('Loading plat data:', platData);
    console.log('Number of lots:', platData.lots.length);
    console.log('Total area:', platData.area);

    // Draw lot boundaries
    this.drawLots(platData.lots);
    
    // Draw easements
    this.drawEasements(platData.easements);
    
    // Draw streets
    this.drawStreets(platData.streets);
    
    // Add lot labels
    this.addLotLabels(platData.lots);
    
    // Add house placement button only
    this.addHousePlacementButton();
    
    // Fit map to plat bounds
    this.fitMapToBounds();
  }
  
  /**
   * Add house placement button only
   */
  private addHousePlacementButton(): void {
    // Create a simple button control
    const controlDiv = document.createElement('div');
    controlDiv.style.cssText = `
      margin: 10px;
      padding: 0;
    `;

    const controlUI = document.createElement('div');
    controlUI.style.cssText = `
      background-color: white;
      border: 2px solid #fff;
      border-radius: 8px;
      box-shadow: 0 2px 6px rgba(0,0,0,.3);
      padding: 8px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
    `;
    
    controlUI.innerHTML = `
      <button id="house-placement-btn" style="
        padding: 10px 16px;
        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
        color: white;
        border: none;
        border-radius: 6px;
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        transition: transform 0.2s;
      " onmouseover="this.style.transform='scale(1.02)'" onmouseout="this.style.transform='scale(1)'">
        🏗️ Select Floor Plan
      </button>
    `;
    
    controlDiv.appendChild(controlUI);
    
    // Add click handler for house placement button
    setTimeout(() => {
      const btn = document.getElementById('house-placement-btn');
      if (btn) {
        btn.addEventListener('click', () => {
          this.toggleHousePlacement();
        });
      }
    }, 100);
    
    // Add the control to the map
    this.map.controls[google.maps.ControlPosition.TOP_RIGHT].push(controlDiv);
  }

  /**
   * Draw lot boundaries on the map with area information
   */
  private drawLots(lots: PlatLot[]): void {
    lots.forEach(lot => {
      // Filter out invalid coordinates
      const validBoundary = lot.boundary.filter(coord => 
        this.isValidCoordinate(coord.lat, coord.lng)
      );
      
      // Skip if not enough valid points to form a polygon
      if (validBoundary.length < 3) {
        console.warn(`Skipping lot ${lot.lotNumber} - insufficient valid coordinates`);
        return;
      }
      
      const polygon = new google.maps.Polygon({
        paths: validBoundary.map(coord => ({
          lat: coord.lat,
          lng: coord.lng
        })),
        strokeColor: '#2563eb', // Blue for GIS boundary
        strokeOpacity: 0.9,
        strokeWeight: 3,
        fillColor: '#3b82f6',
        fillOpacity: 0.15,
        map: this.map,
        clickable: true
      });
      
      // Add a second polygon to show the buildable area (60% inner boundary)
      if (lot.area && lot.area > 0) {
        // Create an inset boundary for buildable area visualization
        const centroid = this.calculateCentroid(validBoundary);
        const buildableBoundary = validBoundary.map(coord => ({
          lat: centroid.lat + (coord.lat - centroid.lat) * 0.85,
          lng: centroid.lng + (coord.lng - centroid.lng) * 0.85
        }));
        
        const buildablePolygon = new google.maps.Polygon({
          paths: buildableBoundary,
          strokeColor: '#16a34a', // Green for buildable area
          strokeOpacity: 0.7,
          strokeWeight: 2,
          strokeDashArray: [10, 5],
          fillColor: '#22c55e',
          fillOpacity: 0.1,
          map: this.map,
          clickable: false
        });
        
        this.overlays.lots.push(buildablePolygon);
      }

      // Add info window with area details
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="font-family: system-ui; padding: 12px; min-width: 280px;">
            <h3 style="margin: 0 0 12px 0; font-size: 18px; font-weight: 600; color: #1f2937;">
              Lot ${lot.lotNumber}${lot.block ? ` - Block ${lot.block}` : ''}
            </h3>
            <div style="background: #f3f4f6; padding: 12px; border-radius: 8px; margin-bottom: 12px;">
              <h4 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600; color: #374151;">Area Information</h4>
              <div style="space-y: 4px;">
                <div style="display: flex; justify-content: space-between; margin: 4px 0;">
                  <span style="color: #6b7280; font-size: 13px;">Total Area:</span>
                  <span style="font-weight: 600; color: #111827; font-size: 13px;">${lot.area ? lot.area.toLocaleString() : 'N/A'} sq ft</span>
                </div>
                <div style="display: flex; justify-content: space-between; margin: 4px 0;">
                  <span style="color: #6b7280; font-size: 13px;">Buildable (60%):</span>
                  <span style="font-weight: 600; color: #059669; font-size: 13px;">${lot.area ? (lot.area * 0.6).toLocaleString() : 'N/A'} sq ft</span>
                </div>
              </div>
            </div>
            <div style="background: #fef3c7; padding: 12px; border-radius: 8px; margin-bottom: 12px;">
              <h4 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600; color: #92400e;">Dimensions</h4>
              <div style="display: flex; justify-content: space-between; margin: 4px 0;">
                <span style="color: #92400e; font-size: 13px;">Frontage:</span>
                <span style="font-weight: 600; color: #451a03; font-size: 13px;">${lot.frontage || 'N/A'} ft</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin: 4px 0;">
                <span style="color: #92400e; font-size: 13px;">Depth:</span>
                <span style="font-weight: 600; color: #451a03; font-size: 13px;">${lot.depth || 'N/A'} ft</span>
              </div>
            </div>
            ${lot.zoning ? `
              <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                <span style="color: #6b7280; font-size: 13px;">Zoning:</span>
                <span style="font-weight: 600; color: #111827; font-size: 13px;">${lot.zoning}</span>
              </div>
            ` : ''}
            ${lot.floodZone ? `
              <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                <span style="color: #6b7280; font-size: 13px;">Flood Zone:</span>
                <span style="font-weight: 600; color: #111827; font-size: 13px;">${lot.floodZone}</span>
              </div>
            ` : ''}
          </div>
        `
      });

      // Add click handler to show info window
      polygon.addListener('click', (e: google.maps.MapMouseEvent) => {
        // Close any other open info windows first
        this.overlays.lots.forEach((p, i) => {
          if (p !== polygon) {
            p.setOptions({ fillOpacity: 0.1 });
          }
        });
        
        // Open info window at click position
        infoWindow.setPosition(e.latLng!);
        infoWindow.open(this.map);
        
        // Highlight this polygon
        polygon.setOptions({
          fillOpacity: 0.4,
          strokeWeight: 3,
          strokeColor: '#16a34a'
        });
        
        // Log for debugging
        console.log('Lot clicked:', lot.lotNumber, 'Area:', lot.area);
      });

      // Add hover effect
      polygon.addListener('mouseover', () => {
        polygon.setOptions({
          fillOpacity: 0.3,
          strokeWeight: 3
        });
      });

      polygon.addListener('mouseout', () => {
        if (this.selectedLot !== lot) {
          polygon.setOptions({
            fillOpacity: 0.1,
            strokeWeight: 2
          });
        }
      });

      this.overlays.lots.push(polygon);
    });
  }

  /**
   * Draw easements on the map
   */
  private drawEasements(easements: PlatEasement[]): void {
    easements.forEach(easement => {
      const color = this.getEasementColor(easement.type);
      
      const polygon = new google.maps.Polygon({
        paths: easement.boundary.map(coord => ({
          lat: coord.lat,
          lng: coord.lng
        })),
        strokeColor: color,
        strokeOpacity: 0.6,
        strokeWeight: 1,
        fillColor: color,
        fillOpacity: 0.15,
        map: this.map,
        clickable: true,
        strokeDashArray: [5, 5] // Dashed line
      });

      // Add info window on click
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="font-family: system-ui; padding: 8px;">
            <h4 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">
              ${easement.type.charAt(0).toUpperCase() + easement.type.slice(1)} Easement
            </h4>
            <p style="margin: 4px 0; font-size: 12px; color: #666;">
              ${easement.description}
            </p>
            <p style="margin: 4px 0; font-size: 12px; color: #666;">
              Width: ${easement.width} ft
            </p>
            ${easement.grantee ? `
              <p style="margin: 4px 0; font-size: 12px; color: #666;">
                Grantee: ${easement.grantee}
              </p>
            ` : ''}
          </div>
        `
      });

      polygon.addListener('click', (e: google.maps.MapMouseEvent) => {
        infoWindow.setPosition(e.latLng!);
        infoWindow.open(this.map);
      });

      this.overlays.easements.push(polygon);
    });
  }

  /**
   * Draw streets on the map
   */
  private drawStreets(streets: PlatStreet[]): void {
    streets.forEach(street => {
      const polyline = new google.maps.Polyline({
        path: street.centerline.map(coord => ({
          lat: coord.lat,
          lng: coord.lng
        })),
        strokeColor: '#6b7280', // Gray
        strokeOpacity: 0.8,
        strokeWeight: 4,
        map: this.map
      });

      // Add street label
      if (street.centerline.length > 0) {
        const midPoint = street.centerline[Math.floor(street.centerline.length / 2)];
        const label = new google.maps.Marker({
          position: { lat: midPoint.lat, lng: midPoint.lng },
          map: this.map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 0
          },
          label: {
            text: street.name,
            color: '#374151',
            fontSize: '11px',
            fontWeight: '500'
          }
        });
        this.overlays.labels.push(label);
      }

      this.overlays.streets.push(polyline);
    });
  }

  /**
   * Add lot labels to the map with area information
   */
  private addLotLabels(lots: PlatLot[]): void {
    lots.forEach(lot => {
      // Calculate centroid of lot
      const centroid = this.calculateCentroid(lot.boundary);
      
      // Create a custom label with area information
      const labelText = lot.area ? 
        `Lot ${lot.lotNumber}\n${lot.area.toLocaleString()} sq ft` :
        `Lot ${lot.lotNumber}`;
      
      const label = new google.maps.Marker({
        position: centroid,
        map: this.map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 0
        },
        label: {
          text: labelText,
          color: '#1f2937',
          fontSize: '12px',
          fontWeight: '600'
        }
      });

      this.overlays.labels.push(label);
    });
  }

  /**
   * Draw setback lines for a selected lot
   */
  public drawSetbackLines(lot: PlatLot, setbacks: any): void {
    // Clear existing setback lines
    this.clearSetbackLines();

    if (!this.platData) return;

    // Calculate setback boundaries
    const setbackBoundaries = this.calculateSetbackBoundaries(lot.boundary, setbacks);
    
    setbackBoundaries.forEach(boundary => {
      const polyline = new google.maps.Polyline({
        path: boundary.path,
        strokeColor: boundary.color,
        strokeOpacity: 0.7,
        strokeWeight: 2,
        strokeDashArray: [10, 5],
        map: this.map
      });

      this.overlays.setbackLines.push(polyline);
    });
  }

  /**
   * Select a lot and highlight it
   */
  public selectLot(lot: PlatLot): void {
    // Reset previous selection
    if (this.selectedLot) {
      const prevIndex = this.platData?.lots.findIndex(l => l === this.selectedLot);
      if (prevIndex !== undefined && prevIndex >= 0) {
        this.overlays.lots[prevIndex].setOptions({
          fillOpacity: 0.1,
          strokeWeight: 2,
          strokeColor: '#2563eb'
        });
      }
    }

    // Highlight selected lot
    this.selectedLot = lot;
    const index = this.platData?.lots.findIndex(l => l === lot);
    if (index !== undefined && index >= 0) {
      this.overlays.lots[index].setOptions({
        fillOpacity: 0.3,
        strokeWeight: 3,
        strokeColor: '#16a34a' // Green for selected
      });

      // Pan to lot
      const centroid = this.calculateCentroid(lot.boundary);
      this.map.panTo(centroid);
    }

    // Draw setback lines if available
    if (this.platData?.setbacks) {
      this.drawSetbackLines(lot, this.platData.setbacks);
    }
  }

  /**
   * Calculate setback boundaries from lot boundaries
   */
  private calculateSetbackBoundaries(
    lotBoundary: Array<{lat: number, lng: number}>,
    setbacks: any
  ): Array<{path: google.maps.LatLngLiteral[], color: string}> {
    const boundaries = [];
    
    // This is a simplified calculation
    // In reality, you'd need proper geometric calculations
    // to create inset boundaries based on setback distances
    
    // Front setback (example - would need proper calculation)
    if (setbacks.front) {
      boundaries.push({
        path: [
          lotBoundary[0],
          lotBoundary[1]
        ],
        color: '#ef4444' // Red for front
      });
    }

    // Add more setback lines as needed...
    
    return boundaries;
  }

  /**
   * Calculate the centroid of a polygon
   */
  private calculateCentroid(points: Array<{lat: number, lng: number}>): google.maps.LatLngLiteral {
    let lat = 0;
    let lng = 0;
    
    points.forEach(point => {
      lat += point.lat;
      lng += point.lng;
    });
    
    return {
      lat: lat / points.length,
      lng: lng / points.length
    };
  }

  /**
   * Get color based on easement type
   */
  private getEasementColor(type: string): string {
    const colors: Record<string, string> = {
      utility: '#f59e0b', // Amber
      drainage: '#06b6d4', // Cyan
      access: '#8b5cf6', // Purple
      conservation: '#10b981', // Green
      other: '#6b7280' // Gray
    };
    
    return colors[type] || colors.other;
  }

  /**
   * Check if coordinates are valid (not in ocean or null island)
   */
  private isValidCoordinate(lat: number, lng: number): boolean {
    // Check for null island (0,0) or invalid coordinates
    if (Math.abs(lat) < 1 && Math.abs(lng) < 1) {
      return false;
    }
    // Check for reasonable US coordinates
    if (lat < 24 || lat > 50 || lng < -130 || lng > -65) {
      return false;
    }
    return true;
  }

  /**
   * Fit map to plat boundaries
   */
  private fitMapToBounds(): void {
    if (!this.platData) return;
    
    const bounds = new google.maps.LatLngBounds();
    let validCoordinates = 0;
    
    // Validate coordinates before extending bounds
    this.platData.lots.forEach(lot => {
      lot.boundary.forEach(coord => {
        // Check for valid coordinates (not 0,0 or invalid)
        if (this.isValidCoordinate(coord.lat, coord.lng)) {
          bounds.extend({ lat: coord.lat, lng: coord.lng });
          validCoordinates++;
        }
      });
    });
    
    // Only fit bounds if we have valid coordinates
    if (validCoordinates > 0) {
      this.map.fitBounds(bounds);
    } else {
      console.error('No valid plat coordinates found. Map will not be repositioned.');
    }
  }

  /**
   * Clear setback lines
   */
  private clearSetbackLines(): void {
    this.overlays.setbackLines.forEach(line => line.setMap(null));
    this.overlays.setbackLines = [];
  }

  /**
   * Clear all overlays
   */
  public clearOverlays(): void {
    // Clear lots
    this.overlays.lots.forEach(polygon => polygon.setMap(null));
    this.overlays.lots = [];
    
    // Clear easements
    this.overlays.easements.forEach(polygon => polygon.setMap(null));
    this.overlays.easements = [];
    
    // Clear streets
    this.overlays.streets.forEach(polyline => polyline.setMap(null));
    this.overlays.streets = [];
    
    // Clear labels
    this.overlays.labels.forEach(marker => marker.setMap(null));
    this.overlays.labels = [];
    
    // Clear setback lines
    this.clearSetbackLines();
    
    this.selectedLot = null;
  }

  /**
   * Toggle visibility of overlays
   */
  public setVisible(visible: boolean): void {
    this.isVisible = visible;
    
    if (visible && this.platData) {
      this.loadPlatData(this.platData);
    } else {
      this.clearOverlays();
    }
  }

  /**
   * Get selected lot
   */
  public getSelectedLot(): PlatLot | null {
    return this.selectedLot;
  }

  /**
   * Toggle floor plan placement tool
   */
  private toggleHousePlacement(): void {
    const btn = document.getElementById('house-placement-btn');
    
    if (this.floorPlanTool) {
      // Disable tool
      this.floorPlanTool.disable();
      this.floorPlanTool = null;
      if (btn) {
        btn.innerHTML = '🏗️ Select Floor Plan';
        btn.style.background = 'linear-gradient(135deg, #3b82f6, #1d4ed8)';
      }
    } else {
      // Enable tool
      if (this.platData && this.platData.lots.length > 0) {
        const lot = this.selectedLot || this.platData.lots[0];
        this.floorPlanTool = new FloorPlanPlacementTool(this.map, lot.boundary);
        
        if (btn) {
          btn.innerHTML = '❌ Close Floor Plans';
          btn.style.background = 'linear-gradient(135deg, #ef4444, #dc2626)';
        }
      } else {
        alert('No property boundary data available for floor plan placement.');
      }
    }
  }

  /**
   * Destroy the overlay
   */
  public destroy(): void {
    this.clearOverlays();
    this.platData = null;
    if (this.floorPlanTool) {
      this.floorPlanTool.disable();
      this.floorPlanTool = null;
    }
  }
}